import { useState, useMemo, useRef, useCallback, useEffect } from 'react';

import { subscribe } from 'lib/EventBuser';
import { REFRESH_IMPORT_AFTER_CREATE_FIELD } from 'lib/events';
import {
    getImportSchema,
    getImport,
    updateSchema,
    sendMatching,
    runImport,
    getTableSchema,
} from 'lib/services/imports';
import { showErrorToast } from 'lib/errors';
import { getExtraFieldEntities } from 'lib/services/fields';
import { getEntityFromString } from 'utils/getEntityFromString';

import ToFieldFormatter from '../../formatters/ToFieldFormatter';
import FieldFormatter from '../../formatters/FieldFormatter';
import StatusFormatter from '../../formatters/StatusFormatter';

function getOptions(tableSchema, mandatoryStandardLabel, mandatoryExtraLabel, optionalLabel) {
    let options = [];
    const splitted = Object.keys(tableSchema).reduce(
        (obj, key) => {
            let label = tableSchema[key].name;
            if (tableSchema[key].mandatory) {
                const mandatory = tableSchema[key].standard
                    ? 'mandatoryStandard'
                    : 'mandatoryExtra';
                obj[mandatory].push({
                    label: `${label}*`,
                    value: key,
                });
            } else {
                obj.others.push({
                    label,
                    value: key,
                });
            }
            return obj;
        },
        {
            mandatoryStandard: [],
            mandatoryExtra: [],
            others: [],
        },
    );

    const haveMandatoryStandard = splitted.mandatoryStandard.length > 0;
    const haveMandatoryExtra = splitted.mandatoryExtra.length > 0;

    if (haveMandatoryStandard || haveMandatoryExtra) {
        haveMandatoryStandard &&
            options.push({
                label: mandatoryStandardLabel,
                options: splitted.mandatoryStandard,
            });
        haveMandatoryExtra &&
            options.push({
                label: mandatoryExtraLabel,
                options: splitted.mandatoryExtra,
            });
        options.push({
            label: optionalLabel,
            options: splitted.others,
        });
    } else {
        options = [...options, ...splitted.others];
    }

    return options;
}

function checkRequired(schema, matchValues, matchMandatoryExtra) {
    const errorKeys = Object.keys(schema.tableSchema).reduce((keys, key) => {
        if (
            schema.tableSchema[key].mandatory &&
            !matchValues.current.includes(key) &&
            (schema.tableSchema[key].standard ||
                (matchMandatoryExtra && !schema.tableSchema[key].standard))
        ) {
            keys.push(key);
        }
        return keys;
    }, []);
    return errorKeys;
}

const checkExtraInfo = (data) => {
    if (!data) return;
    let found = false;
    data.forEach((obj) => {
        if (obj.extraInfo) {
            found = Object.values(obj.extraInfo).find((value) => value === -1);
            if (found) found = true;
        }
    });
    return found;
};

function checkMatches(schema, mandatories) {
    return !!schema.matchingSchema.find(
        (item) =>
            item.nameField &&
            item.extraInfo &&
            Object.keys(item.extraInfo)
                .map((key) => item.extraInfo[key])
                .find((value) => {
                    const standard = schema.tableSchema[item.nameField]?.standard;
                    const mandatory = schema.tableSchema[item.nameField]?.mandatory;
                    const createStandardMandatory =
                        schema.info?.type === 'create' &&
                        standard &&
                        mandatory &&
                        mandatories.matchMandatoryStandard;
                    const createExtraMandatory =
                        schema.info?.type === 'create' &&
                        !standard &&
                        mandatory &&
                        mandatories.matchMandatoryExtra;
                    const updateMandatory =
                        schema.info?.type === 'update' && mandatory && mandatories.matchMandatory;
                    const invalid =
                        value === -1 &&
                        (createStandardMandatory || createExtraMandatory || updateMandatory);
                    return invalid;
                }),
    );
}

const initialRefs = {
    timeout: null,
    matchOptions: [],
    matchValues: [],
    currentMatches: {},
    schema: null,
    syncNames: [],
};

const initialState = {
    errors: null,
    currentState: undefined,
    isLoading: false,
    isProcessing: false,
    isFinishing: false,
    isInvalid: true,
};

const useMatchFields = ({
    data,
    entityLabel,
    t,
    onComplete,
    onBack,
    onChange,
    logEvent,
    entityName,
    entityLogName,
    onImported,
    maxImportColumns,
    maxImportRows,
    currentStep,
    idSetupRole,
    language,
}) => {
    const pollingStateTimeout = useRef(null);
    const pollingFinalStateTimeout = useRef(null);
    const matchOptions = useRef([]);
    const availableOptions = useRef([]);
    const matchValues = useRef([]);
    const currentMatches = useRef({});
    const matchingTableRef = useRef(null);
    const [matchingModalRef, setMatchingModalRef] = useState(null);
    const schema = useRef();
    const syncNames = useRef([]);
    const [errors, setErrors] = useState(null);
    const [currentState, setCurrentState] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [isProcessing, setIsProcessing] = useState(data.state === 1);
    const [isFinishing, setIsFinishing] = useState(false);
    const [isInvalid, setIsInvalid] = useState(true);
    const [width, setWidth] = useState(document.body.scrollWidth);
    const [modalConfirmIsOpen, setModalConfirmIsOpen] = useState(false);
    const [formExtraFieldRef, setFormExtraFieldRef] = useState(null);
    const [entitiesForExtraFieldsForm, setEntitiesForExtraFieldsForm] = useState(null);
    const [controlOpenedMatchingModal, setControlOpenedMatchingModal] = useState({});
    const [isUpdatingDraft, setIsUpdatingDraft] = useState(false);
    const [isDraftUpdated, setIsDraftUpdated] = useState(false);

    const resetAll = useCallback(() => {
        pollingStateTimeout.current = initialRefs.timeout;
        pollingFinalStateTimeout.current = initialRefs.timeout;

        matchOptions.current = initialRefs.matchOptions;
        availableOptions.current = initialRefs.availableOptions;
        matchValues.current = initialRefs.matchValues;
        currentMatches.current = initialRefs.currentMatches;
        schema.current = initialRefs.schema;
        setErrors(initialState.errors);
        setIsProcessing(initialState.isProcessing);
        setCurrentState(initialState.currentState || 1);
        setIsInvalid(initialState.isInvalid);
        setFormExtraFieldRef(null);
    }, []);

    const handleOnBack = useCallback(() => {
        resetAll();
        onBack();
    }, [resetAll, onBack]);

    const mandatories = useMemo(
        () => ({
            matchMandatory: data.matchMandatory,
            matchMandatoryStandard: data.matchMandatoryStandard,
            matchMandatoryExtra: data.matchMandatoryExtra,
        }),
        [data.matchMandatory, data.matchMandatoryExtra, data.matchMandatoryStandard],
    );

    const checkIsInvalid = useCallback(
        () =>
            checkMatches(
                {
                    ...schema.current,
                    matchingSchema: schema.current.matchingSchema.map((item, idx) => ({
                        name: item.name,
                        nameField: matchValues.current[idx],
                        extraInfo: currentMatches.current[item.name],
                        id: item.name,
                    })),
                },
                mandatories,
            ),
        [mandatories],
    );

    useEffect(() => {
        if (
            data.importType === null ||
            data.matchMandatory !== undefined ||
            data.matchMandatoryStandard !== undefined
        )
            return;
        if (data.importType === 'create') {
            onChange({
                matchMandatory: false,
                matchMandatoryStandard:
                    data.matchMandatory !== undefined ? data.matchMandatory : true,
                matchMandatoryExtra: data.matchMandatory !== undefined ? data.matchMandatory : true,
            });
        } else {
            onChange({
                matchMandatory: data.matchMandatory !== undefined ? data.matchMandatory : false,
                matchMandatoryStandard: false,
                matchMandatoryExtra: false,
            });
        }
    }, [data, data.importType, data.matchMandatory, data.matchMandatoryStandard, onChange]);

    const initSchemaProcess = useCallback(
        (s) => {
            matchOptions.current = getOptions(
                s.tableSchema,
                t('cfm_label_required_standard'),
                t('cfm_label_required_cutom'),
                t('cfm_label_optional'),
            );

            matchValues.current = s.matchingSchema.map((item) => {
                if (item.nameField) return item.nameField;
                return '';
            });

            availableOptions.current = matchOptions.current[0].options
                ? matchOptions.current.map((group) => ({
                      label: group.label,
                      options: group.options.filter(
                          (el) => !matchValues.current.includes(el.value),
                      ),
                  }))
                : matchOptions.current.filter((el) => !matchValues.current.includes(el.value));
            const namesDB = Object.values(s.tableSchema).reduce(
                (acc, el) => ({
                    ...acc,
                    [el.nameApi?.toLowerCase()]: el.name,
                }),
                {},
            );

            syncNames.current = s.info?.syncNames?.map(
                (el) => namesDB[el] || namesDB[el.toLowerCase()] || [],
            );

            // Extracting matches if exists in a structure like { field: { ...matches} }
            currentMatches.current = {
                ...currentMatches.current,
                ...s.matchingSchema.reduce((obj, item) => {
                    if (item.extraInfo) {
                        obj[item.name] = item.extraInfo;
                    }
                    return obj;
                }, {}),
            };

            if (s?.matchingSchema)
                s.matchingSchema = s.matchingSchema?.map((el) => ({
                    ...el,
                    id: el.name,
                }));
            schema.current = s;
        },
        [t],
    );

    const init = useCallback(
        (s, isForField) => {
            if (!isForField) {
                initSchemaProcess(s);
                return Promise.resolve();
            }
            return getTableSchema(entityName, data.importType, language)
                .then((result) => {
                    const tableSchema = result.data;
                    const newSchema = { ...s, tableSchema };
                    initSchemaProcess(newSchema);
                })
                .catch(() => initSchemaProcess(s));
        },
        [initSchemaProcess, data.importType, entityName, language],
    );

    // Update draft in order to have included all the fields created after generating the draft
    const updateDraft = useCallback(
        (shouldRefreshCells) => {
            setIsUpdatingDraft(true);
            let newSchema = {};
            let tableSchema = {};
            return getTableSchema(entityName, data.importType, language)
                .then((result) => {
                    tableSchema = result.data;
                    newSchema = { ...schema.current, tableSchema };
                    return updateSchema(data.id, newSchema);
                })
                .then(() => {
                    initSchemaProcess(newSchema);

                    if (shouldRefreshCells) {
                        const gridApi = matchingTableRef.current.getApi();
                        gridApi.refreshCells({ columns: ['nameField', 'extraInfo'], force: true });
                    }
                })
                .catch(() => initSchemaProcess(schema.current))
                .finally(() => {
                    setIsUpdatingDraft(false);
                    setIsDraftUpdated(true);
                });
        },
        [entityName, data.importType, language, data.id, initSchemaProcess],
    );

    const onChangeMatch = useCallback(
        (index, value, node, api) => {
            const newMatchValues = [...matchValues.current];
            newMatchValues[index] = value;
            matchValues.current = newMatchValues;
            const extraInfo = value
                ? (currentMatches.current[node.data.name] &&
                      Object.keys(currentMatches.current?.[node.data.name]).reduce((obj, key) => {
                          obj[key] = -1;
                          return obj;
                      }, {})) ||
                  {}
                : undefined;
            node.setData({
                name: node.data.name,
                nameField: value || null,
                extraInfo,
            });
            availableOptions.current = matchOptions.current[0].options
                ? matchOptions.current.map((group) => ({
                      label: group.label,
                      options: group.options.filter(
                          (el) => !matchValues.current.includes(el.value),
                      ),
                  }))
                : matchOptions.current.filter((el) => !matchValues.current.includes(el.value));
            api.refreshCells({ columns: ['nameField', 'extraInfo'], force: true });
            setIsInvalid(checkIsInvalid());
        },
        [checkIsInvalid],
    );

    useEffect(() => {
        return subscribe(REFRESH_IMPORT_AFTER_CREATE_FIELD, ({ field, rowIndex }) => {
            const matchingSchemaNew =
                schema.current.matchingSchema?.map((item, idx) => ({
                    name: item.name,
                    nameField: matchValues.current[idx],
                    extraInfo: matchValues.current[idx]
                        ? currentMatches.current[item.name]
                        : undefined,
                })) || [];

            schema.current = { ...schema.current, matchingSchema: matchingSchemaNew };
            init(schema.current, true).finally(() => {
                const gridApi = matchingTableRef.current.getApi();
                if (!schema?.current?.matchingSchema?.[rowIndex]?.name) return;
                const rowNode = gridApi.getRowNode(
                    String(schema.current.matchingSchema[rowIndex].name),
                );

                const type = field.type;
                if (['list', 'multivalue'].includes(type)) {
                    const matchAffected =
                        currentMatches.current[schema.current.matchingSchema[rowIndex].name];
                    if (matchAffected && Object.keys(matchAffected).length) {
                        const newMatchAffected = Object.keys(matchAffected).reduce((obj, key) => {
                            obj[key] = '';
                            return obj;
                        }, {});
                        currentMatches.current[schema.current.matchingSchema[rowIndex].name] =
                            newMatchAffected;
                        schema.current.matchingSchema[rowIndex] = {
                            ...schema.current.matchingSchema[rowIndex],
                            extraInfo: newMatchAffected,
                        };
                    }
                } else {
                    delete currentMatches.current[schema.current.matchingSchema[rowIndex].name];
                    delete schema.current.matchingSchema[rowIndex]?.extraInfo;
                }
                schema.current.matchingSchema[rowIndex] = {
                    ...schema.current.matchingSchema[rowIndex],
                    nameField: field.description,
                };
                updateDraft().then(() => {
                    onChangeMatch(rowIndex, field.description, rowNode, gridApi);
                });
            });
        });
    }, [t, data.importType, entityName, language, init, onChangeMatch, updateDraft]);

    // Polling after state = 1
    const pollingState = useCallback(
        (data, cb, cbErr) => {
            setIsProcessing(true);
            pollingStateTimeout.current = setTimeout(() => {
                getImport(data.id)
                    .then((result) => {
                        if (result.data.state === 1) {
                            return pollingState(result.data, cb, cbErr);
                        } else {
                            clearTimeout(pollingStateTimeout.current);
                            return getImportSchema(data.id);
                        }
                    })
                    .then((result) => {
                        if (result) {
                            init(result.data).finally(() => {
                                setCurrentState(2);
                                setIsProcessing(false);
                                setIsLoading(false);
                                cb && cb(result.data);
                            });
                        }
                    })
                    .catch((error) => {
                        cbErr && cbErr(error);
                        setIsProcessing(false);
                        setIsLoading(false);
                    });
            }, 2000);
        },
        [init],
    );

    const pollingFinalState = useCallback((data, cb, cbErr) => {
        pollingFinalStateTimeout.current = setTimeout(() => {
            getImport(data.id)
                .then((result) => {
                    if (result.data.state === 5) {
                        clearTimeout(pollingFinalStateTimeout.current);
                        return cb && cb(result.data);
                    } else {
                        return pollingFinalState(result.data, cb, cbErr);
                    }
                    // return result.data.state === 5
                    //     ? cb && cb(result.data)
                    //     : pollingFinalState(result.data, cb, cbErr);
                })
                .catch((error) => {
                    cbErr && cbErr(error);
                });
        }, 2000);
    }, []);

    const onFinish = useCallback(() => {
        setIsFinishing(true);
        runImport(data.id)
            .then(() => {
                logEvent({
                    event: entityLogName,
                    submodule: entityName,
                    functionality: 'importUpdateFinish',
                });
                onImported(data.id);
            })
            .catch((error) => {
                console.error(error);
                showErrorToast(t, error);
            });
    }, [data.id, entityLogName, entityName, logEvent, onImported, t]);

    const onValidate = useCallback(() => {
        setIsProcessing(true);
        // Checking required
        const matchMandatoryExtra = data.importType === 'update' ? true : data.matchMandatoryExtra;
        const errorKeys =
            data.importType === 'update' && !data.matchMandatory
                ? []
                : checkRequired(schema.current, matchValues, matchMandatoryExtra);
        if (errorKeys.length) {
            setIsProcessing(false);
            setIsInvalid(true);
            return setErrors([t('cfm_error_mandatory_fields', [errorKeys.join(', ')])]);
        }

        // Merge current matching values
        const newSchema = {
            ...schema.current,
            matchingSchema: schema.current.matchingSchema.map((item, idx) => ({
                name: item.name,
                nameField: matchValues.current[idx],
                extraInfo: matchValues.current[idx] ? currentMatches.current[item.name] : undefined,
            })),
        };

        if (checkMatches(newSchema, mandatories)) {
            setIsInvalid(true);
            setIsProcessing(false);
            return setErrors([t('cfm_error_matching_fields')]);
        }

        // Back to schema processing state
        updateSchema(data.id, newSchema)
            .then(() => sendMatching(data.id))
            .then(() => {
                return new Promise((resolve, reject) => {
                    pollingState(
                        data,
                        (result) => {
                            resolve(result);
                        },
                        (error) => {
                            reject(error);
                        },
                    );
                });
            })
            .then((processedSchema) => {
                const inValidMatches = checkMatches(processedSchema, mandatories);
                const notMatched = checkExtraInfo(
                    processedSchema.matchingInfo || processedSchema.matchingSchema,
                );

                if (notMatched || inValidMatches) {
                    schema.current = processedSchema;
                    setErrors([t('cfm_error_matching_fields')]);
                    setIsInvalid(true);
                } else {
                    data.importType === 'update' && setModalConfirmIsOpen(true);
                    data.importType === 'create' &&
                        onComplete({
                            state: 3,
                            schema: processedSchema,
                        });

                    logEvent({
                        event: entityLogName,
                        submodule: entityName,
                        functionality: 'importCreateLinkFields',
                    });
                }
            })
            .catch((error) => {
                handleOnBack();
                showErrorToast(t, error);
            });
    }, [
        data,
        mandatories,
        t,
        pollingState,
        onComplete,
        handleOnBack,
        logEvent,
        entityLogName,
        entityName,
    ]);

    const setData = useCallback(
        (info) => {
            let idType;
            const importType = data?.importType || info?.type;
            if (importType === 'create') {
                idType = null;
            } else if (info?.syncNames?.[0] === 'id') {
                idType = 'id';
            } else {
                idType = 'others';
            }

            onChange({
                importType,
                idType,
                columns: info?.columns,
                rows: info?.rows,
                syncNames: importType === 'update' ? info?.syncNames : [],
                syncNamesMatch: info?.syncNamesMatch,
                matchMandatory:
                    data.matchMandatory !== undefined ? data.matchMandatory : info?.matchMandatory,
                matchMandatoryStandard:
                    data.matchMandatoryStandard !== undefined
                        ? data.matchMandatoryStandard
                        : info?.mandatoryStandardFields,
                matchMandatoryExtra:
                    data.matchMandatoryExtra !== undefined
                        ? data.matchMandatoryExtra
                        : info?.mandatoryExtraFields,
            });
        },
        [data, onChange],
    );

    useEffect(() => {
        if (!currentState && data.state) {
            setCurrentState(data.state);
        }
    }, [currentState, data]);

    // Get schema
    useEffect(() => {
        // Only first time
        if (!currentState || currentStep !== 3 || matchValues.current.length || isLoading) return;
        setIsLoading(true);
        if (currentState === 1) {
            new Promise((resolve, reject) => {
                pollingState(
                    data,
                    (result) => {
                        resolve(result);
                    },
                    (error) => {
                        reject(error);
                    },
                );
            })
                .then((result) => {
                    setData(result.info);
                    if (
                        result?.info?.syncNamesMatch?.length ||
                        result?.info?.columns > maxImportColumns ||
                        result?.info?.rows > maxImportRows
                    ) {
                        handleOnBack();
                    }
                })
                .catch((error) => {
                    handleOnBack();
                    showErrorToast(t, error);
                });
        } else {
            getImportSchema(data.id)
                .then((result) => {
                    init(result.data).finally(() => {
                        setIsLoading(false);
                        setData(result.data.info);
                    });
                })
                .catch((error) => {
                    console.error(error);
                    showErrorToast(t, error);
                    handleOnBack();
                });
        }
    }, [
        currentStep,
        currentState,
        pollingState,
        data,
        init,
        t,
        onBack,
        handleOnBack,
        isLoading,
        setData,
        maxImportColumns,
        maxImportRows,
    ]);

    const onEdit = useCallback(
        (data, matchFieldsNode, matchFieldsApi) => {
            matchingModalRef.open(data, schema.current, matchFieldsNode, matchFieldsApi);
        },
        [matchingModalRef],
    );

    const onConfirm = useCallback(
        (field) => {
            if (!controlOpenedMatchingModal[field.name]) {
                setControlOpenedMatchingModal({
                    ...controlOpenedMatchingModal,
                    [field.name]: true,
                });
            }
            currentMatches.current = { ...currentMatches.current, [field.name]: field.extraInfo };
            setIsInvalid(checkIsInvalid());
        },
        [checkIsInvalid, controlOpenedMatchingModal],
    );

    useEffect(() => {
        if (
            schema.current &&
            data?.matchMandatory !== undefined &&
            data?.matchMandatoryStandard !== undefined &&
            data?.matchMandatoryExtra !== undefined
        ) {
            setIsInvalid(checkIsInvalid());
        }
    }, [checkIsInvalid, data]);

    const toggleMatchMandatory = useCallback(() => {
        onChange({ matchMandatory: !data.matchMandatory });
    }, [data.matchMandatory, onChange]);

    const toggleMatchMandatoryStandard = useCallback(() => {
        onChange({ matchMandatoryStandard: !data.matchMandatoryStandard });
    }, [data.matchMandatoryStandard, onChange]);

    const toggleMatchMandatoryExtra = useCallback(() => {
        onChange({ matchMandatoryExtra: !data.matchMandatoryExtra });
    }, [data.matchMandatoryExtra, onChange]);

    const onCancelConfirm = useCallback(() => {
        setModalConfirmIsOpen(false);
    }, []);

    const loadOptions = useCallback(() => availableOptions.current, []);

    const openCreateFieldModal = useCallback(
        ({ rowIndex }) => {
            const entity = getEntityFromString(entityName);

            if (entitiesForExtraFieldsForm) {
                formExtraFieldRef.open({
                    entities: entitiesForExtraFieldsForm,
                    entity: entity?.fields || entityName,
                    rowIndex,
                });
            } else {
                const getViewsInGetExtraFieldEntities = true;
                getExtraFieldEntities(getViewsInGetExtraFieldEntities).then((response) => {
                    setEntitiesForExtraFieldsForm(response.data || []);
                    formExtraFieldRef.open({
                        entities: response.data || [],
                        entity: entity?.fields || entityName,
                        rowIndex,
                    });
                });
            }
        },
        [entitiesForExtraFieldsForm, formExtraFieldRef, entityName],
    );

    const getMatchOptions = useCallback(() => matchOptions.current, []);

    const getTableSchemaRef = useCallback(() => schema.current?.tableSchema || {}, []);

    const listProps = useMemo(() => {
        const columns = [
            {
                colId: 'name',
                field: 'name',
                headerName: t('cfm_label_your_fields_xls'),
                resizable: true,
                pinned: 'left',
                cellRenderer: 'ToFieldFormatter',
            },
            {
                colId: 'nameField',
                field: 'nameField',
                headerName: t('cfm_label_fm_fields'),
                resizable: true,
                pinned: 'left',
                cellRenderer: 'FieldFormatter',
                cellRendererParams: {
                    getMatchOptions,
                    availableOptions: availableOptions.current,
                    loadOptions,
                    syncNames: syncNames.current,
                    onChange: onChangeMatch,
                    openCreateFieldModal,
                },
            },
            {
                colId: 'extraInfo',
                field: 'extraInfo',
                headerName: t('cfm_label_readed_files'),
                resizable: true,
                pinned: 'left',
                cellRenderer: 'StatusFormatter',
                cellRendererParams: {
                    onEdit,
                    setIsInvalid,
                    getTableSchemaRef,
                    syncNames: syncNames.current,
                    controlOpenedMatchingModal,
                },
            },
        ];

        let props = {
            isLoading,
            isProcessing,
            rows: schema.current ? schema.current.matchingSchema || [] : null,
            columns: [
                {
                    colId: 'space1',
                    resizable: false,
                    pinned: 'left',
                },
                ...columns,
                {
                    colId: 'space2',
                    resizable: false,
                    pinned: 'left',
                },
            ],
            components: {
                ToFieldFormatter,
                FieldFormatter,
                StatusFormatter,
            },
            options: {
                suppressRowHoverHighlight: true,
                rowHeight: 40,
                rowBuffer: 20,
            },
            onRef: (ref) => {
                matchingTableRef.current = ref;
            },
        };

        if (width <= 1024) {
            props.columns = columns;
        }

        return props;
    }, [
        isLoading,
        isProcessing,
        loadOptions,
        onChangeMatch,
        onEdit,
        t,
        width,
        openCreateFieldModal,
        controlOpenedMatchingModal,
        getMatchOptions,
        getTableSchemaRef,
    ]);

    const modalConfirmProps = useMemo(() => {
        const modifiedFields = matchValues.current?.filter(Boolean).join(', ');

        return {
            onConfirm: onFinish,
            onCancel: onCancelConfirm,
            isOpen: modalConfirmIsOpen,
            title: `${t('cfm_lable_confirm_import_title')} ${t('cfm_label_import_type_update', [
                entityLabel,
            ])}`,
            description: t('confirm_info_num_account_edited', [data.rows, entityLabel]),
            advice: `${t('cfm_label_import_update_fields_info', [modifiedFields])}\\n${t(
                'label_integration_disclaimer_import_update',
            )}`,
            writeXToConfirm: 'label_type_confirmation',
            passPhrase: t('cfm_label_update'),
            confirmText: t('cfm_action_confirm_import_update', [data.rows, entityLabel]),
            isConfirmLoading: isFinishing,
        };
    }, [data.rows, entityLabel, isFinishing, modalConfirmIsOpen, onCancelConfirm, onFinish, t]);

    const matchFieldsProps = useMemo(() => {
        return {
            currentState,
            isInvalid,
            listProps,
            onConfirm,
            modalConfirmProps,
            matchingModalRef,
            setMatchingModalRef,
            setWidth,
        };
    }, [currentState, isInvalid, listProps, onConfirm, modalConfirmProps, matchingModalRef]);

    const matchFieldsAdviceProps = useMemo(
        () => ({
            isUpdatingDraft,
            isDraftUpdated,
            updateDraft,
            currentState,
        }),
        [isUpdatingDraft, currentState, isDraftUpdated, updateDraft],
    );

    const matchFieldsHeaderProps = useMemo(() => {
        return {
            errors,
            toggleMatchMandatory,
            toggleMatchMandatoryExtra,
            toggleMatchMandatoryStandard,
        };
    }, [errors, toggleMatchMandatory, toggleMatchMandatoryExtra, toggleMatchMandatoryStandard]);

    const matchFieldsFooterProps = useMemo(() => {
        return {
            onConfirm: onValidate,
            isConfirmDisabled: isInvalid,
        };
    }, [onValidate, isInvalid]);

    const matchFieldsCreateModalProps = useMemo(() => {
        return {
            idSetupRole,
            formExtraFieldRef,
            setFormExtraFieldRef,
        };
    }, [idSetupRole, formExtraFieldRef, setFormExtraFieldRef]);

    if (currentStep !== 3) return {};

    return {
        matchFieldsProps,
        matchFieldsAdviceProps,
        matchFieldsHeaderProps,
        matchFieldsFooterProps,
        matchFieldsCreateModalProps,
        onBackMatchFields: handleOnBack,
        isMatchingOpen: isProcessing,
    };
};

export default useMatchFields;
